import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import LandingNav from '@/pages/home/components/LandingNav';

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('Privacy Policy');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('site_settings')
        .select('key, value')
        .in('key', ['privacy_policy_title', 'privacy_policy_content', 'site_name']);
      if (data) {
        const map: Record<string, string> = {};
        data.forEach((r: { key: string; value: string }) => { map[r.key] = r.value; });
        if (map.privacy_policy_title) setTitle(map.privacy_policy_title);
        if (map.privacy_policy_content) setContent(map.privacy_policy_content);
      }
      setLoading(false);
    }
    load();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <LandingNav />
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-xs text-gray-400 mb-6">
              <button onClick={() => navigate('/')} className="hover:text-gray-600 cursor-pointer transition-colors">Home</button>
              <i className="ri-arrow-right-s-line" />
              <span className="text-gray-600 font-medium">{title}</span>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              {/* Header */}
              <div className="bg-sidebar px-8 py-10">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-shield-user-line text-white text-2xl" />
                </div>
                <h1 className="text-2xl font-extrabold text-white">{title}</h1>
                <p className="text-white/50 text-sm mt-1">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>

              {/* Content */}
              <div className="px-8 py-8">
                {loading ? (
                  <div className="space-y-3 animate-pulse">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div key={i} className={`h-4 bg-gray-100 rounded ${i % 3 === 2 ? 'w-3/4' : 'w-full'}`} />
                    ))}
                  </div>
                ) : content ? (
                  <div
                    className="prose prose-sm max-w-none text-gray-700 leading-relaxed text-sm rte-render"
                    // If content starts with '<' it's HTML from rich text editor, otherwise render as plain text
                    dangerouslySetInnerHTML={
                      content.trim().startsWith('<')
                        ? { __html: content }
                        : { __html: content.split('\n').map(l => `<p>${l}</p>`).join('') }
                    }
                  />
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <i className="ri-file-text-line text-4xl block mb-3 text-gray-200" />
                    <p className="text-sm">Privacy policy content has not been configured yet.</p>
                    <p className="text-xs mt-1">Please check back later or contact support.</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-gray-100 px-8 py-5 bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-xs text-gray-400">If you have questions, contact us at <span className="text-brand-600">support@apimarts.com</span></p>
                <button
                  onClick={() => navigate('/terms')}
                  className="text-xs text-brand-500 hover:text-brand-600 cursor-pointer flex items-center gap-1 whitespace-nowrap"
                >
                  View Terms &amp; Conditions <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
